/** Reporting engine — Widget Builder.
 *
 * A right-side drawer: pick a type, a metric/dimension/board, optional filters;
 * live-preview against the current month; add to the dashboard. Ported from the
 * mockup. Filter options derive from the loaded month so only real values show.
 *
 * Pass `initial` to edit an existing widget instead of building a new one. This
 * is the ONLY place a widget's filters can be set: the Focus Explorer is a read
 * view, so anything pinned here is what every client sees on the shared global
 * template.
 */

import { useMemo, useState } from "react";
import type { CSSProperties } from "react";

import type { ReportingTicketRow } from "../services/api";

import {
  C,
  DIMENSIONS,
  LEADERBOARDS,
  METRICS,
  MONO,
  PRIORITY_COLOR,
  SANS,
  distinctValues,
} from "./data";
import type { BoardKey, DimensionKey, Filters, MetricKey, Widget, WidgetType } from "./types";
import { WidgetBody } from "./widgets";

type Row = ReportingTicketRow;

const TYPE_DEFS: { type: WidgetType; name: string; desc: string; glyph: string }[] = [
  { type: "kpi", name: "KPI tile", desc: "One number + trend", glyph: "42" },
  { type: "ring", name: "Gauge", desc: "One value, as a dial", glyph: "◐" },
  { type: "line", name: "Trend", desc: "Daily opened vs closed", glyph: "╱╲" },
  { type: "bar", name: "Breakdown", desc: "Counts by category", glyph: "≣" },
  { type: "leaderboard", name: "Leaderboard", desc: "Top people, ranked", glyph: "①" },
  { type: "table", name: "Ticket list", desc: "Raw rows, scrollable", glyph: "▤" },
  { type: "csat", name: "Satisfaction", desc: "Ratings by type", glyph: "★" },
];

const DEFAULT_SIZE: Record<WidgetType, { w: number; h: number }> = {
  kpi: { w: 3, h: 2 },
  ring: { w: 4, h: 2 },
  line: { w: 8, h: 4 },
  bar: { w: 4, h: 4 },
  leaderboard: { w: 4, h: 4 },
  table: { w: 12, h: 5 },
  csat: { w: 4, h: 3 },
};

function defaultTitle(d: {
  type: WidgetType;
  metric: MetricKey;
  dimension: DimensionKey;
  board: BoardKey;
}): string {
  if (d.type === "kpi" || d.type === "ring") return METRICS[d.metric]?.label ?? "Metric";
  if (d.type === "bar") return DIMENSIONS[d.dimension]?.label ?? "Breakdown";
  if (d.type === "leaderboard") return LEADERBOARDS[d.board]?.label ?? "Leaderboard";
  if (d.type === "line") return "The shape of the month";
  if (d.type === "table") return "Tickets";
  if (d.type === "csat") return "Customer satisfaction";
  return "Widget";
}

function PickRow({ active, onClick, title, sub }: { active: boolean; onClick: () => void; title: string; sub?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        font: "inherit",
        cursor: "pointer",
        textAlign: "left",
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        marginBottom: 6,
        borderRadius: 2,
        border: `1px solid ${active ? C.forest : C.rule}`,
        background: active ? "#f0f6ef" : "#fff",
      }}
    >
      <span>
        <span style={{ display: "block", fontSize: 13, color: C.ink, fontWeight: active ? 600 : 400 }}>{title}</span>
        {sub && <span style={{ display: "block", fontSize: 10.5, color: C.stone, fontFamily: MONO, marginTop: 1 }}>{sub}</span>}
      </span>
      {active && <span style={{ color: C.forest, fontSize: 14 }}>✓</span>}
    </button>
  );
}

function MiniChip({ active, onClick, children, color }: { active: boolean; onClick: () => void; children: string; color?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        font: "inherit",
        cursor: "pointer",
        padding: "4px 9px",
        fontSize: 10.5,
        fontFamily: MONO,
        borderRadius: 2,
        border: `1px solid ${active ? color || C.ink : C.rule}`,
        background: active ? color || C.ink : "#fff",
        color: active ? "#fff" : C.mud,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

const section: CSSProperties = {
  fontFamily: MONO,
  fontSize: 10.5,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: C.ink,
  fontWeight: 600,
  margin: "18px 0 10px",
  borderBottom: `1px solid ${C.ink}`,
  paddingBottom: 5,
};

type ChipGroup = "priority" | "issueType" | "source" | "status";
const CHIP_GROUPS: ChipGroup[] = ["priority", "issueType", "source", "status"];
const GROUP_LABEL: Record<ChipGroup, string> = {
  priority: "Priority",
  issueType: "Issue type",
  source: "Source",
  status: "Status",
};

export function WidgetBuilder({
  monthTickets,
  days,
  onAdd,
  onClose,
  initial,
  onSave,
}: {
  monthTickets: Row[];
  days: number;
  onAdd: (w: Widget) => void;
  onClose: () => void;
  // Present when editing an existing widget rather than building a new one.
  initial?: Widget | null;
  onSave?: (w: Widget) => void;
}) {
  const editing = initial != null;
  const [type, setType] = useState<WidgetType>(initial?.type ?? "kpi");
  const [metric, setMetric] = useState<MetricKey>(initial?.metric ?? "opened");
  const [dimension, setDimension] = useState<DimensionKey>(initial?.dimension ?? "priority");
  const [board, setBoard] = useState<BoardKey>(initial?.board ?? "contact");
  const [filters, setFilters] = useState<Record<ChipGroup, string[]>>({
    priority: initial?.filters?.priority ?? [],
    issueType: initial?.filters?.issueType ?? [],
    source: initial?.filters?.source ?? [],
    status: initial?.filters?.status ?? [],
  });
  // Filters the chip rows don't manage (sub-type, and the day range the old
  // drill-down slicer could pin). Carried through an edit rather than silently
  // dropped, with a visible way to clear them since nothing else can now.
  const [extra, setExtra] = useState<Partial<Filters>>(() => {
    const f = initial?.filters ?? {};
    const out: Partial<Filters> = {};
    if (f.subType?.length) out.subType = f.subType;
    if (f.dayFrom != null && f.dayFrom > 1) out.dayFrom = f.dayFrom;
    if (f.dayTo != null && f.dayTo < days) out.dayTo = f.dayTo;
    return out;
  });
  const [title, setTitle] = useState(initial?.title ?? "");

  const values: Record<ChipGroup, string[]> = {
    priority: distinctValues(monthTickets, "priority"),
    issueType: distinctValues(monthTickets, "issueType"),
    source: distinctValues(monthTickets, "source"),
    status: distinctValues(monthTickets, "status"),
  };

  // Only non-empty groups are written back, so a widget with no filters keeps a
  // clean `{}` rather than four empty arrays.
  const composed = useMemo<Partial<Filters>>(() => {
    const f: Partial<Filters> = { ...extra };
    for (const g of CHIP_GROUPS) if (filters[g].length) f[g] = filters[g];
    return f;
  }, [filters, extra]);

  const draft = useMemo<Widget>(
    () => ({
      id: "preview",
      type,
      metric,
      dimension,
      board,
      filters: composed,
      title: title || defaultTitle({ type, metric, dimension, board }),
      pos: { x: 0, y: 0, ...DEFAULT_SIZE[type] },
      locked: false,
    }),
    [type, metric, dimension, board, composed, title],
  );

  const toggle = (group: ChipGroup, v: string) =>
    setFilters((f) => {
      const arr = f[group];
      return { ...f, [group]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
    });

  const commit = () => {
    const next = {
      type,
      metric,
      dimension,
      board,
      filters: composed,
      title: title || defaultTitle({ type, metric, dimension, board }),
    };
    if (editing && onSave) {
      // Size, position, lock and drill-down state belong to the layout, not to
      // this drawer, so an edit leaves them exactly as they were.
      onSave({ ...initial, ...next });
      return;
    }
    onAdd({
      ...next,
      id: "w" + Date.now().toString(36),
      pos: { x: 0, y: 999, ...DEFAULT_SIZE[type] },
      locked: false,
    });
  };

  const showMetric = type === "kpi" || type === "ring";
  const showDim = type === "bar";
  const showBoard = type === "leaderboard";
  const activeFilters = CHIP_GROUPS.reduce((n, g) => n + filters[g].length, 0);
  const dayPinned = extra.dayFrom != null || extra.dayTo != null;
  const stepNo = showMetric || showDim || showBoard ? "3" : "2";

  return (
    <div style={scrim} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div style={drawer}>
        <div style={drawerHead}>
          <div>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: C.stone, marginBottom: 4 }}>
              {editing ? "Edit widget" : "Build a widget"}
            </div>
            <div style={{ fontFamily: SANS, fontWeight: 600, fontSize: 20, color: C.ink }}>
              {editing ? initial.title || "Widget" : "Add to dashboard"}
            </div>
          </div>
          <button type="button" style={closeX} onClick={onClose}>
            ✕
          </button>
        </div>

        <div style={{ padding: "18px 22px", overflow: "auto", flex: 1 }}>
          <div style={section}>1 · Widget type</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {TYPE_DEFS.map((t) => (
              <button
                key={t.type}
                type="button"
                onClick={() => setType(t.type)}
                style={{
                  font: "inherit",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  padding: 12,
                  borderRadius: 3,
                  textAlign: "left",
                  border: `1px solid ${type === t.type ? C.forest : C.rule}`,
                  background: type === t.type ? "#f0f6ef" : "#fff",
                }}
              >
                <span style={{ fontFamily: MONO, fontSize: 18, lineHeight: 1, color: type === t.type ? C.forest : C.stone }}>{t.glyph}</span>
                <span style={{ fontSize: 13, color: C.ink, fontWeight: 500, marginTop: 4 }}>{t.name}</span>
                <span style={{ fontSize: 10.5, color: C.stone }}>{t.desc}</span>
              </button>
            ))}
          </div>

          {showMetric && (
            <>
              <div style={section}>2 · Metric</div>
              <div style={{ maxHeight: 220, overflow: "auto", marginBottom: 4, paddingRight: 2 }}>
                {(Object.keys(METRICS) as MetricKey[]).map((k) => (
                  <PickRow
                    key={k}
                    active={metric === k}
                    onClick={() => setMetric(k)}
                    title={METRICS[k].label}
                    sub={METRICS[k].kind}
                  />
                ))}
              </div>
            </>
          )}
          {showDim && (
            <>
              <div style={section}>2 · Breakdown by</div>
              {(Object.keys(DIMENSIONS) as DimensionKey[]).map((k) => (
                <PickRow key={k} active={dimension === k} onClick={() => setDimension(k)} title={DIMENSIONS[k].label} sub={DIMENSIONS[k].key} />
              ))}
            </>
          )}
          {showBoard && (
            <>
              <div style={section}>2 · Rank</div>
              {(Object.keys(LEADERBOARDS) as BoardKey[]).map((k) => (
                <PickRow key={k} active={board === k} onClick={() => setBoard(k)} title={LEADERBOARDS[k].label} sub={LEADERBOARDS[k].key} />
              ))}
            </>
          )}
          {(type === "line" || type === "table" || type === "csat") && (
            <div style={{ fontSize: 12, color: C.mud, background: "#f7f5f0", border: `1px solid ${C.rule}`, padding: "10px 12px", borderRadius: 2, lineHeight: 1.5, marginTop: 8 }}>
              This widget shows{" "}
              {type === "line"
                ? "daily opened vs closed across the period"
                : type === "csat"
                  ? "how many Fantastic / Good / Neutral / Review ratings came back on the work completed this period"
                  : "the raw ticket rows"}
              . Add filters below to scope it.
            </div>
          )}

          <div style={section}>
            {stepNo} · Filters <span style={{ color: C.stone, fontWeight: 400 }}>(optional)</span>
          </div>
          <div>
            {CHIP_GROUPS.map((g) =>
              values[g].length ? (
                <div key={g}>
                  <div style={fLabel}>{GROUP_LABEL[g]}</div>
                  <div style={chipRow}>
                    {values[g].map((v) => (
                      <MiniChip
                        key={v}
                        active={filters[g].includes(v)}
                        color={g === "priority" ? PRIORITY_COLOR[v] : undefined}
                        onClick={() => toggle(g, v)}
                      >
                        {v}
                      </MiniChip>
                    ))}
                  </div>
                </div>
              ) : null,
            )}
            {(dayPinned || extra.subType?.length) && (
              <div style={extraNote}>
                <span>
                  Also pinned:{" "}
                  {[
                    extra.subType?.length ? `sub-category (${extra.subType.length})` : null,
                    dayPinned ? `days ${extra.dayFrom ?? 1}–${extra.dayTo ?? days}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <button type="button" style={clearBtn} onClick={() => setExtra({})}>
                  Clear
                </button>
              </div>
            )}
          </div>

          <div style={section}>Title</div>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={defaultTitle({ type, metric, dimension, board })}
            style={{ width: "100%", boxSizing: "border-box", font: "inherit", fontFamily: SANS, fontSize: 14, padding: "9px 11px", border: `1px solid ${C.rule}`, borderRadius: 2, color: C.ink }}
          />
        </div>

        <div style={{ borderTop: `1px solid ${C.rule}`, padding: "16px 22px 20px", background: "#fcfbf8" }}>
          <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: C.stone, marginBottom: 10 }}>
            Live preview {activeFilters > 0 && <span style={{ color: C.stone }}>· {activeFilters} filter{activeFilters > 1 ? "s" : ""}</span>}
          </div>
          <div style={{ background: "#fff", border: `1px solid ${C.rule}`, borderRadius: 3, padding: "12px 14px", marginBottom: 14, boxShadow: "0 1px 2px rgba(20,20,20,0.04)" }}>
            <div style={{ fontFamily: SANS, fontWeight: 600, fontSize: 12, color: C.ink, textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: `1px solid ${C.rule}`, paddingBottom: 8, marginBottom: 10 }}>
              {draft.title}
            </div>
            <div style={{ height: DEFAULT_SIZE[type].h <= 2 ? 96 : 168, minHeight: 0 }}>
              <WidgetBody widget={draft} tickets={monthTickets} days={days} big={type === "line" || type === "ring"} />
            </div>
          </div>
          <button type="button" style={addBtn} onClick={commit}>
            {editing ? "Save changes" : "＋ Add to dashboard"}
          </button>
        </div>
      </div>
    </div>
  );
}

const scrim: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(20,20,18,0.42)",
  backdropFilter: "blur(3px)",
  WebkitBackdropFilter: "blur(3px)",
  zIndex: 210,
  display: "flex",
  justifyContent: "flex-end",
};
const drawer: CSSProperties = {
  width: "min(460px, 100%)",
  height: "100%",
  background: "#fff",
  boxShadow: "-24px 0 80px rgba(20,20,20,0.3)",
  display: "flex",
  flexDirection: "column",
  borderLeft: `1px solid ${C.rule}`,
};
const drawerHead: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "18px 22px",
  borderBottom: `1px solid ${C.rule}`,
};
const closeX: CSSProperties = {
  font: "inherit",
  cursor: "pointer",
  width: 32,
  height: 32,
  border: `1px solid ${C.rule}`,
  background: "#fff",
  color: C.mud,
  borderRadius: 2,
  fontSize: 14,
};
const fLabel: CSSProperties = { fontSize: 10.5, color: C.mud, fontFamily: MONO, margin: "10px 0 6px" };
const chipRow: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 6 };
const extraNote: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  marginTop: 12,
  padding: "8px 10px",
  background: "#f7f5f0",
  border: `1px solid ${C.rule}`,
  borderRadius: 2,
  fontFamily: MONO,
  fontSize: 10.5,
  color: C.mud,
};
const clearBtn: CSSProperties = {
  font: "inherit",
  cursor: "pointer",
  padding: "3px 9px",
  background: "#fff",
  color: C.orange,
  border: `1px solid ${C.orange}`,
  borderRadius: 2,
  whiteSpace: "nowrap",
};
const addBtn: CSSProperties = {
  font: "inherit",
  cursor: "pointer",
  width: "100%",
  padding: 12,
  fontSize: 14,
  fontWeight: 600,
  fontFamily: SANS,
  background: C.ink,
  color: C.green,
  border: "none",
  borderRadius: 2,
};
